import React, { useEffect, useState } from 'react';
import { RefreshCw, ShieldAlert, MessageSquareReply } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { apiService, formatApiError } from '../services/api';
import { fmtDate } from './AdminModeration';

const SIGNAL_LABEL = {
  disposable_email_domain: 'disposable email domain', placeholder_name_and_subject: 'placeholder name + subject', too_few_words: 'fewer than 3 words',
  many_links: '3+ links', contains_link: 'contains a link', spam_keywords: 'spam keywords', repeated_characters: 'repeated characters',
  ip_velocity: 'many submissions from one IP', email_velocity: 'many submissions from one email', duplicate_submission: 'duplicate text',
};

export const AdminSpamLog = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = () => apiService.admin.getSpamLog({ limit: 100 }).then((r) => { setData(r.data.data); setError(''); }).catch((e) => setError(formatApiError(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4" data-testid="admin-spam-log">
      <Card className="p-4 text-sm text-gray-700 flex flex-wrap items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium">Rejected contact-form submissions</p>
          <p className="text-gray-600">A submission is rejected only when its weighted score reaches {data?.reject_threshold ?? 3}. A short name or an unfamiliar email domain is never enough on its own. Only a masked email, masked IP, score and signals are kept — never the message text — and entries expire after {data?.retention_days ?? 30} days.</p>
          {data && <p className="text-xs text-gray-500 mt-1" data-testid="spam-log-stats">{data.total} rejected · {data.flagged_accepted_open} accepted-but-flagged tickets currently open</p>}
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="spam-log-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </Card>
      {error && <p className="text-red-600 text-sm" data-testid="spam-log-error">{error}</p>}
      {data && Object.keys(data.by_signal).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="spam-log-signals">
          {Object.entries(data.by_signal).sort((a, b) => b[1] - a[1]).map(([k, n]) => <span key={k} className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800">{SIGNAL_LABEL[k] || k}: {n}</span>)}
        </div>
      )}
      {data && data.items.length === 0 && <Card className="p-6 text-center text-gray-500" data-testid="spam-log-empty">No rejected submissions in the last {data.retention_days} days.</Card>}
      {data?.items.map((it) => (
        <Card key={it.id} className="p-3 text-sm flex flex-wrap items-center gap-3" data-testid={`spam-log-row-${it.id}`}>
          <span className="text-xs uppercase text-gray-500">{it.kind}</span>
          <span className="font-mono text-gray-700">{it.email_masked}</span>
          <span className="text-gray-500">{it.client_ip_masked || '—'}</span>
          <span className="font-semibold text-amber-800">score {it.score}</span>
          <span className="text-gray-600">{(it.signals || []).map((s) => SIGNAL_LABEL[s] || s).join(', ')}</span>
          <span className="text-xs text-gray-400">{it.link_count} link(s) · {it.message_length} chars{it.subject_preview ? ` · "${it.subject_preview}"` : ''}</span>
          <span className="ml-auto text-xs text-gray-400">{fmtDate(it.created_at)}</span>
        </Card>
      ))}
    </div>
  );
};

export const AdminMemberReplies = ({ onOpenMember }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = () => apiService.admin.getUnansweredThreads().then((r) => { setData(r.data.data); setError(''); }).catch((e) => setError(formatApiError(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="admin-member-replies">
      <div className="flex items-center justify-between"><p className="text-sm text-gray-600">Conversations where the member replied last and nobody has looked yet.</p><Button size="sm" variant="outline" onClick={load} data-testid="member-replies-refresh"><RefreshCw className="w-4 h-4" /></Button></div>
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {data && data.threads.length === 0 && <Card className="p-6 text-center text-gray-500" data-testid="member-replies-empty">No unanswered member replies.</Card>}
      {data?.threads.map((t) => (
        <Card key={t.id} className="p-3 text-sm flex flex-wrap items-center gap-3" data-testid={`member-reply-thread-${t.id}`}>
          <MessageSquareReply className="w-4 h-4 text-rose-600" />
          <span className="font-semibold">{t.member_name || 'Member'}</span>
          <span className="text-gray-700">{t.subject}</span>
          <span className="text-gray-500 truncate max-w-md">{t.replies?.[0]?.body}</span>
          <span className="ml-auto text-xs text-gray-400">{fmtDate(t.last_reply_at)}</span>
          <Button size="sm" variant="outline" onClick={() => onOpenMember?.(t.user_id, 'messages')} data-testid={`member-reply-open-${t.id}`}>Open thread</Button>
        </Card>
      ))}
    </div>
  );
};
